import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

const Index = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/images/icon48.png" alt="Football Scores" className="w-10 h-10" />
            <h1 className="text-xl font-bold text-gray-900">Football Live Scores</h1>
          </div>
          <nav className="flex items-center gap-4">
            <Link to="/privacy">
              <Button variant="ghost">Privacy Policy</Button>
            </Link>
            <Button className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-glow))]">
              Add to Chrome
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <Badge className="mb-4 bg-[hsl(var(--secondary))]" variant="secondary">
            ⚽ Chrome Extension
          </Badge>
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
            Live Football Scores
            <br />
            Right in Your Browser
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Stay updated with real-time scores, standings, and match schedules from all major leagues worldwide. 
            One-time purchase, no subscriptions.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button size="lg" className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-glow))] text-lg px-8 py-6">
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 15.894l-1.789 1.789L12 13.578l-4.105 4.105-1.789-1.789L10.211 12 6.106 7.895l1.789-1.789L12 10.211l4.105-4.105 1.789 1.789L13.789 12l4.105 4.105z"/>
              </svg>
              Add to Chrome - $4.99
            </Button>
            <Button size="lg" variant="outline" className="text-lg px-8 py-6">
              View Demo
            </Button>
          </div>
          
          {/* Extension Preview */}

        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h3 className="text-4xl font-bold text-center mb-12">Powerful Features</h3>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-2 hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h4 className="text-xl font-bold mb-2">{feature.title}</h4>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Leagues Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <h3 className="text-4xl font-bold text-center mb-12">Supported Leagues</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {leagues.map((league, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="text-3xl mb-2">{league.flag}</div>
                  <p className="font-semibold">{league.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h3 className="text-4xl font-bold text-center mb-12">How It Works</h3>
          <div className="space-y-8">
            {steps.map((step, index) => (
              <div key={index} className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center font-bold text-xl">
                  {index + 1}
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2">{step.title}</h4>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h3 className="text-4xl font-bold mb-6">Simple, One-Time Pricing</h3>
          <p className="text-xl text-gray-600 mb-12">No subscriptions. No hidden fees. Buy once, use forever.</p>
          
          <Card className="max-w-md mx-auto border-4 border-[hsl(var(--primary))] shadow-xl">
            <CardContent className="pt-8 pb-8">
              <Badge className="mb-4 bg-[hsl(var(--secondary))]">BEST VALUE</Badge>
              <div className="text-5xl font-bold mb-2">$4.99</div>
              <p className="text-gray-600 mb-6">One-time purchase</p>
              <ul className="text-left space-y-3 mb-8">
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span> Live scores & updates
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span> All major leagues
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span> Match notifications
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span> Dark mode
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span> Lifetime updates
                </li>
              </ul>
              <Button className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-glow))] text-lg py-6">
                Get Started Now
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h3 className="text-4xl font-bold mb-6">Ready to Never Miss a Match?</h3>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of football fans who stay updated with our extension
          </p>
          <Button size="lg" className="bg-white text-green-600 hover:bg-gray-100 text-lg px-8 py-6">
            Add to Chrome - $4.99
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/images/icon48.png" alt="Logo" className="w-8 h-8" />
                <span className="font-bold">Football Live Scores</span>
              </div>
              <p className="text-gray-400">
                Real-time football scores and standings for all major leagues.
              </p>
            </div>
            <div>
              <h5 className="font-bold mb-4">Quick Links</h5>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => scrollToSection('features')} className="hover:text-white cursor-pointer">Features</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white cursor-pointer">Pricing</button></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-4">Support</h5>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => scrollToSection('how-it-works')} className="hover:text-white cursor-pointer">How It Works</button></li>
                <li><Link to="/contact" className="hover:text-white">Contact Us</Link></li>
                <li><a href="https://chrome.google.com/webstore" target="_blank" rel="noopener noreferrer" className="hover:text-white">Chrome Web Store</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>© 2025 Football Live Scores. All rights reserved.</p>
            <p className="mt-2">Powered by API-Football</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const features = [
  {
    icon: "⚡",
    title: "Live Scores",
    description: "Real-time match updates with automatic 30-second refresh for all live games"
  },
  {
    icon: "📊",
    title: "League Standings",
    description: "Complete standings tables with wins, draws, losses, and goal difference"
  },
  {
    icon: "🔔",
    title: "Match Notifications",
    description: "Get instant alerts when matches start and when goals are scored"
  },
  {
    icon: "🏆",
    title: "10+ Major Leagues",
    description: "Premier League, La Liga, Serie A, Bundesliga, Champions League, and more"
  },
  {
    icon: "🌙",
    title: "Dark Mode",
    description: "Beautiful dark theme that's easy on the eyes during night matches"
  },
  {
    icon: "⏱️",
    title: "World Cup Countdown",
    description: "Live countdown to FIFA World Cup 2026 with real-time seconds"
  }
];

const leagues = [
  { flag: "🏴󠁧󠁢󠁥󠁮󠁧󠁿", name: "Premier League" },
  { flag: "🇪🇸", name: "La Liga" },
  { flag: "🇮🇹", name: "Serie A" },
  { flag: "🇩🇪", name: "Bundesliga" },
  { flag: "🇫🇷", name: "Ligue 1" },
  { flag: "🏆", name: "Champions League" },
  { flag: "🇺🇸", name: "MLS" },
  { flag: "🌍", name: "World Cup" }
];

const steps = [
  {
    title: "Install Extension",
    description: "Click 'Add to Chrome' and complete the one-time $4.99 purchase through Chrome Web Store"
  },
  {
    title: "Select Your League",
    description: "Choose from 10+ major football leagues including Premier League, La Liga, and Champions League"
  },
  {
    title: "Stay Updated",
    description: "Get live scores, standings, and notifications automatically. Toggle dark mode anytime!"
  }
];

export default Index;
